import { useState } from 'react';

export interface UseAutoRefreshOptions {
  defaultEnabled?: boolean;
  interval?: number; // in milliseconds
}

export interface UseAutoRefreshReturn {
  enabled: boolean;
  setEnabled: (enabled: boolean) => void;
  refetchInterval: number | false; // For use in useQuery
}

/**
 * Custom hook for managing auto-refresh state
 * 
 * @param options - Configuration options
 * @param options.defaultEnabled - Whether auto-refresh should be enabled by default (default: false)
 * @param options.interval - Refresh interval in milliseconds (default: 5000)
 * @returns Object containing enabled state, setter, and computed refetchInterval
 * 
 * @example
 * ```tsx
 * const { enabled, setEnabled, refetchInterval } = useAutoRefresh({ 
 *   defaultEnabled: true, 
 *   interval: 10000 
 * });
 * 
 * const { data } = useQuery({
 *   queryKey: ['data'],
 *   queryFn: fetchData,
 *   refetchInterval, // Use the computed interval
 * });
 * ```
 */
export function useAutoRefresh(options?: UseAutoRefreshOptions): UseAutoRefreshReturn {
  const { defaultEnabled = false, interval = 5000 } = options || {};
  const [enabled, setEnabled] = useState(defaultEnabled);

  return {
    enabled,
    setEnabled,
    refetchInterval: enabled ? interval : false,
  };
}

